/**
 * Typed wrapper for all Vite environment variables.
 * Single source of truth — import `env` instead of accessing import.meta.env directly.
 *
 * Usage:
 *   import { env } from '@/shared/config/env';
 *   const apiUrl = env.API_URL;
 */
// Deployment environment — validated to the known set so a typo in the env
// var can't produce a bogus domain suffix. Unset/unknown → 'dev', so a build
// that forgets the variable points vendors at dev storefronts, never at real
// production ones.
const rawAppEnv = ((import.meta.env.VITE_APP_ENV as string) || '')
  .trim()
  .toLowerCase();
const appEnv: 'dev' | 'stg' | 'prod' =
  rawAppEnv === 'stg' || rawAppEnv === 'prod' ? rawAppEnv : 'dev';

export const env = {
  // Auth0
  AUTH0_DOMAIN: import.meta.env.VITE_AUTH0_DOMAIN as string || '',
  AUTH0_CLIENT_ID: import.meta.env.VITE_AUTH0_CLIENT_ID as string || '',
  AUTH0_AUDIENCE: import.meta.env.VITE_AUTH0_AUDIENCE as string || '',
  AUTH0_SCOPE: import.meta.env.VITE_AUTH0_SCOPE as string || 'openid profile email offline_access',

  // API
  API_URL: import.meta.env.VITE_API_URL as string || 'http://localhost:5001/api/v2',
  NOTIFICATION_API_URL: import.meta.env.VITE_NOTIFICATION_API_URL as string || 'http://localhost:5002/api/v1',

  // Feature flags
  /** Beta onboarding: enroll new vendors straight onto the STARTER plan
   *  (PLAN step sends `planCode: "STARTER"` with no `trialEndsAt`) and the
   *  final onboarding step presents the Starter plan instead of the free
   *  trial. Off unless the env var is exactly "true". */
  BETA_ONBOARDING: (import.meta.env.VITE_BETA_ONBOARDING_ENABLED as string) === 'true',
  /** Social media integration (Instagram import): the Integrations screen,
   *  the "Import from Instagram" panel on the product forms, and every
   *  surface that renders imported media. OFF unless the env var is exactly
   *  "true", so the whole feature is hidden by default while it is held back.
   *
   *  Hiding is non-destructive on purpose: with this off the product forms
   *  OMIT `importedMedia` from their writes rather than sending `[]`, so a
   *  product that already has imported media keeps it server-side and gets it
   *  back the moment the flag is turned on. See `useProductImportedMedia`. */
  SOCIAL_IMPORT: (import.meta.env.VITE_SOCIAL_IMPORT_ENABLED as string) === 'true',
  /** Origin the standalone Template Studio SPA is served from. Used to build
   *  the iframe src AND to validate every inbound postMessage. Must match
   *  exactly (scheme + host + port). Never "*". */
  STUDIO_ORIGIN: (import.meta.env.VITE_STUDIO_ORIGIN as string) || '',

  // Deployment environment
  /** "dev" | "stg" | "prod". Defaults to "dev" when VITE_APP_ENV is
   *  unset/unknown. */
  APP_ENV: appEnv,
  /** Storefront domain suffix for the current environment — what the
   *  onboarding wizard and Store Profile append to the vendor's slug:
   *    dev  → .dev.storemandu.com   (also the default when unset/unknown)
   *    stg  → .stg.storemandu.com
   *    prod → .storemandu.com       (production has no env segment)
   *
   *  Fixed 2026-10-08: this used to give prod `.prod.storemandu.com` — a
   *  domain production storefronts are not served on — and gave an unset
   *  VITE_APP_ENV the plain production domain, so a dev build missing the
   *  variable showed vendors real production URLs. */
  STORE_URL_SUFFIX:
    appEnv === 'prod' ? '.storemandu.com' : `.${appEnv}.storemandu.com`,

  // Vite meta
  MODE: import.meta.env.MODE as 'development' | 'test' | 'production',
  DEV: import.meta.env.DEV as boolean,
  PROD: import.meta.env.PROD as boolean,
} as const;
